/**
 * Customer Identity, Address & Storefront Modal Reliability — Phase 3.
 * Extended by Contact Validation & Messaging Configuration Recovery.
 *
 * Shared phone + email validation/formatting for the PUBLIC storefront
 * customer/order workflow — WelcomeModal, CheckoutModal, and
 * ShipToAddressForm all import from here now. Login, OTP, and admin
 * forms (e.g. ShippingAdmin's warehouse-address email field) still have
 * their own separate handling and remain untouched by this module —
 * migrating those onto a shared validator is a distinct, unstarted
 * effort, not silently folded into this one.
 *
 * Phone validation/normalization here targets parity with the
 * backend's `services/validation.py::validate_phone` (strict, complete
 * 10-digit US mobile) and `services/customers.py::normalize_phone`
 * (E.164, assume US on a bare 10-digit number) — kept as a second,
 * frontend-side implementation (not a shared import) because the
 * frontend bundle has no access to backend Python modules; the two are
 * intentionally mirrored, not the same code, and were fixed together
 * to close the same production defect (a 7-digit fragment like
 * "882-314-0" was previously accepted as a complete phone number by
 * both sides).
 */

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function digitsOnly(raw) {
  return String(raw || "").replace(/\D/g, "");
}

/**
 * PR #114 audit correction (Aug 2026): mirrors the backend's
 * `services/validation.py::_PHONE_ALLOWED_CHARS_RE`. `digitsOnly` alone
 * silently discards any non-digit character, including letters, so
 * "808-555-1234ext" reduced to exactly 10 digits and passed `isValidPhone`
 * unchanged. A phone field may contain only an optional leading "+",
 * digits, spaces, hyphens, dots, and parens — never letters.
 */
const PHONE_ALLOWED_CHARS_RE = /^\+?[\d\s\-.()]+$/;

/**
 * Formats a US phone number as the customer types: XXX-XXX-XXXX.
 *
 * Product Owner correction (Aug 2026, PR #114 final phone-contract
 * audit): only strips a leading "1" for display when the customer
 * explicitly typed a "+" (deliberate country-code paste) — a BARE
 * 11-digit value with no "+" is no longer silently reformatted into a
 * valid-looking 10-digit display, since it must now be REJECTED by
 * `isValidPhone`, not quietly reinterpreted. Any other non-US-shaped
 * input is returned untouched so a customer typing an international
 * number isn't fought by the formatter — we just never claim it's
 * US-shaped.
 */
export function formatUsPhoneDisplay(raw) {
  const raw_ = String(raw || "");
  const hasPlus = raw_.trim().startsWith("+");
  let digits = digitsOnly(raw_);
  if (hasPlus && digits.length === 11 && digits[0] === "1") digits = digits.slice(1);
  if (digits.length > 10) return raw; // not a supported shape — pass through untouched
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 10);
  if (digits.length <= 3) return part1;
  if (digits.length <= 6) return `${part1}-${part2}`;
  return `${part1}-${part2}-${part3}`;
}

/**
 * E.164-compatible normalization. Mirrors the backend's
 * `services/customers.py::normalize_phone` exactly (fixed alongside
 * this function).
 *
 * Product Owner correction (Aug 2026, PR #114 final phone-contract
 * audit): this platform's customer phone path is US-only, so only two
 * shapes normalize — a bare 10-digit number (assumed US, prefixed
 * `+1`), or an EXPLICIT "+1" followed by exactly 10 digits (the
 * customer deliberately typed/pasted the country code). A bare
 * 11-digit value with no "+" (even one starting with "1") is no
 * longer silently reinterpreted as a country-coded number, and a "+"
 * followed by any other country code/digit count is rejected as
 * unsupported — never guessed at. Returns "" rather than a
 * half-normalized value.
 */
export function toE164(raw) {
  const raw_ = String(raw || "").trim();
  if (!raw_ || !PHONE_ALLOWED_CHARS_RE.test(raw_)) return "";
  const keepPlus = raw_.startsWith("+");
  const digits = digitsOnly(raw_);
  if (!digits) return "";
  let e164;
  if (keepPlus) {
    if (digits.length === 11 && digits[0] === "1") {
      e164 = `+${digits}`;
    } else {
      return "";
    }
  } else if (digits.length === 10) {
    e164 = `+1${digits}`;
  } else {
    return "";
  }
  const totalDigits = e164.length - 1;
  return totalDigits >= 8 && totalDigits <= 15 ? e164 : "";
}

/**
 * Contact Validation & Messaging Configuration Recovery — production
 * evidence: "882-314-0" (7 digits) passed this check under the old
 * 7-15-digit floor and reached Parcel Shipping method selection.
 * Customer-facing ordering and SMS are US-only on this platform today
 * (no international customer phone path exists anywhere in current
 * source), so a complete customer mobile number must reduce to exactly
 * 10 US national digits.
 *
 * Product Owner correction (Aug 2026, PR #114 final phone-contract
 * audit): "must not accept more or fewer than 10 US national digits."
 * Exactly two shapes are accepted — a bare 10-digit number, or an
 * EXPLICIT "+1" followed by exactly 10 digits. A bare 11-digit value
 * with no "+" (even one starting with "1") is now rejected, not
 * silently reinterpreted as a country code. Mirrors the backend's
 * `services/validation.py::validate_phone` boundary exactly.
 */
export function isValidPhone(raw) {
  const raw_ = String(raw || "").trim();
  if (!raw_ || !PHONE_ALLOWED_CHARS_RE.test(raw_)) return false;
  const hasPlus = raw_.startsWith("+");
  let digits = digitsOnly(raw_);
  if (hasPlus) {
    if (digits.length === 11 && digits[0] === "1") {
      digits = digits.slice(1);
    } else {
      return false;
    }
  }
  return digits.length === 10;
}

export function normalizeEmail(raw) {
  return String(raw || "").trim().toLowerCase();
}

export function isValidEmail(raw) {
  return EMAIL_RE.test(String(raw || "").trim());
}
